import React, { memo, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import PdfHighlighter from '~/components/Shared/EcPdfViewerHighlighter/components/PdfHighlighter';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { removeBreaklinesFromText } from '~/utils/strings';

import { TOOLBAR_HEIGHT } from '../constants';
import { InteractionPosition } from '../types/highlight';
import { BaseDocumentViewerProps } from '../types/viewer';
import { useHighlightsMap } from '../useHighlightsMap';
import { useInteractionHighlight } from '../useInteractionHighlight';
import { getInteractionPosition } from '../utils';
import {
  createHighlightTransform,
  defaultPdfHighlighterProps,
  mapPdfHighlights,
  usePdfDocument,
} from './bridge';
import { Toolbar } from './Toolbar';
import { PdfHighlight } from './types';
import { useScrollToHighlight } from './useScrollToHighlight';

export const PdfDocumentViewer = memo(
  ({
    activeHighlightId,
    file,
    highlights = [],
    invalidateActiveHighlightId,
    navigatorLabel,
    onClear,
    onHighlightClick,
    onHighlightCreate,
  }: BaseDocumentViewerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const intl = useIntl();

    const [pdfDocument, isReady] = usePdfDocument(file);

    const [pdfScaleValue, setPdfScaleValue] = useState<number | 'page-width'>();

    const pdfHighlights = useMemo(
      () => mapPdfHighlights(highlights),
      [highlights],
    );

    const highlightsMap = useHighlightsMap(highlights);

    const {
      clearInteractionHighlight,
      renderInteractionHighlightActionsMenu,
      setInteractionHighlight,
    } = useInteractionHighlight({
      containerRef,
      onHighlightClick,
      onHighlightCreate,
    });

    useScrollToHighlight({
      activeHighlightId,
      pdfHighlights,
      deps: [highlights, invalidateActiveHighlightId],
    });

    const handleHighlightClick = onHighlightClick
      ? (pdfHighlight: PdfHighlight, position: InteractionPosition) => {
          const highlight = highlightsMap[pdfHighlight.id];
          if (highlight) {
            onHighlightClick.callback?.(highlight);
            setInteractionHighlight({
              highlight,
              interaction: 'click',
              position,
            });
          }
        }
      : undefined;

    const loadingMessage = intl.formatMessage({
      id: 'PdfDocumentViewer.loadingMessage',
      defaultMessage: 'Loading document…',
      description: 'Loading message while PDF document is being processed',
    });

    const loadingContent = {
      isLoading: !isReady,
      message: loadingMessage,
    };

    return (
      <div ref={containerRef} style={componentStyles.container}>
        <Toolbar
          activeHighlightId={activeHighlightId}
          highlights={highlights}
          navigatorLabel={navigatorLabel}
          searchListenOn={containerRef}
          zoom={{
            scale: pdfScaleValue,
            onChange: setPdfScaleValue,
          }}
          onClear={onClear}
          onHighlightClick={onHighlightClick}
        />
        <Box styles={componentStyles.inner({ toolbarHeight: TOOLBAR_HEIGHT })}>
          <ContentContainer loadingContent={loadingContent}>
            {pdfDocument && (
              <PdfHighlighter
                {...defaultPdfHighlighterProps}
                pdfDocument={pdfDocument}
                pdfScaleValue={
                  pdfScaleValue ? String(pdfScaleValue) : undefined
                }
                highlights={pdfHighlights}
                highlightTransform={createHighlightTransform({
                  activeHighlightId,
                  containerRef,
                  onClick: handleHighlightClick,
                })}
                // @ts-ignore -- onScroll is added onto PdfHighlighter
                onScroll={clearInteractionHighlight}
                onSelectionFinished={(
                  {
                    pageNumber,
                    rects,
                  }: {
                    // vendor is untyped
                    pageNumber: any;
                    rects: any;
                  },
                  _content: any,
                  _hideMenu: Function,
                  createSelectionHighlight: () => void,
                ) => {
                  if (onHighlightCreate) {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                      // This is a function provided by the vendor to transform the selection in a highlight.
                      createSelectionHighlight();
                      const range = selection.getRangeAt(0);
                      const rect = range.getBoundingClientRect();
                      const newHighlight = {
                        id: uuid(),
                        text: removeBreaklinesFromText(
                          selection.toString().trim(),
                        ),
                        pdfCoordinates: {
                          pageNumber,
                          rects,
                        },
                      };

                      const interactionPosition = getInteractionPosition({
                        clientX: rect.left,
                        clientY: rect.top,
                        containerRef,
                      });
                      setInteractionHighlight({
                        highlight: newHighlight,
                        interaction: 'create',
                        position: interactionPosition,
                      });
                      onHighlightCreate.callback?.(
                        newHighlight,
                        interactionPosition,
                      );
                    }
                  }
                  return null;
                }}
              />
            )}
          </ContentContainer>
          {renderInteractionHighlightActionsMenu()}
        </Box>
      </div>
    );
  },
);

const componentStyles = {
  container: {
    height: '100%',
    position: 'relative' as const,
    flexGrow: 1,
  },
  inner: ({ toolbarHeight }: { toolbarHeight: string }) => ({
    position: 'relative',
    '& span.highlight': {
      backgroundColor: 'highlight.search',
    },
    '& span.highlight.selected': {
      backgroundColor: 'highlight.search',
      backgroundImage: 'linear-gradient(rgba( 0, 0, 0, 0.05) 0 0)',
    },
    '& .textLayer': { zIndex: 'auto' },
    ':hover': {
      backgroundImage: 'none',
    },
    ':active': {
      backgroundImage: 'none',
    },
    height: `calc(100% - ${toolbarHeight})`,
    marginTop: '16px',
  }),
};
